import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance"
import DataTableWithPopup from "components/longContentTable"
import { PAGEPATHS } from "../../components/Common/Constant"
import {
  Shield,
  Lock,
  Info,
  CheckCircle,
  XCircle,
  ShieldCheck,
  Network
} from 'lucide-react'
import "./datatables.scss"
import "./WebSecurity.css"

const WebSecurity = (props) => {
    document.title = "Web Security";
    // const [scanId, setScanId] = useState(sessionStorage.getItem('scanId'))
    const { scanId: paramScanId } = useParams();
    const [scanId, setScanId] = useState(paramScanId || sessionStorage.getItem('scanId'));
    const [pshttData, setPshttData] = useState({})
    const [sslyzeData, setSslyzeData] = useState({})
    const [contentSecurityPolicy, setContentSecurityPolicy] = useState('')

    const breadcrumbItems = [
        { title: "Scan List", link: PAGEPATHS.Scanlist },
        { title: sessionStorage.getItem('domain'), link: "#" },
        { title: "Web Security", link: "#" }
    ]

    useEffect(() => {
        props.setBreadcrumbItems('Website Security', breadcrumbItems)
    })

    const fetchWBScans = async (url, table, setHook) => {
        try {
            const { data } = await axiosInstance.get(url)
            let valid
            if (table) {
                valid = Object.entries(data.data).reduce((a, v) => {
                    a.columns.push({
                        label: v[0],
                        field: v[0],
                        sort: 'asc',
                        width: 150
                    })
                    if (typeof v[1] === 'boolean' || v[1] === 0) {
                        a.rows[0][v[0]] = v[1].toString()
                    } else if (v[1] === null) {
                        a.rows[0][v[0]] = 'NaN'
                    } else {
                        a.rows[0][v[0]] = v[1]
                    }
                    return a
                }, { columns: [], rows: [{}] })
            } else {
                valid = data.data["CSP Set for domain"]
            }
            setHook(valid)
        } catch (e) {
            console.error(e);
        }
    }

    useEffect(() => {
        fetchWBScans(`${ApiNames.FETCH_CSP_SCAN}/${scanId}`, false, setContentSecurityPolicy)
        fetchWBScans(`${ApiNames.FETCH_PSHTT_SCAN}/${scanId}`, true, setPshttData)
        fetchWBScans(`${ApiNames.FETCH_SSLYZE_SCAN}/${scanId}`, true, setSslyzeData)
    }, [scanId])

    const isCspEnabled = contentSecurityPolicy && contentSecurityPolicy.toString() === 'true';

    return (
        <React.Fragment>
            <div className="web-security-page">
                {/* Page Header */}
                <div className="web-security-header">
                    <div className="web-security-header-content">
                        <div className="web-security-icon-wrapper">
                            <Shield className="web-security-icon" />
                        </div>
                        <div className="web-security-header-text">
                            <h1>Web Security Overview</h1>
                            <p>Comprehensive security analysis including HTTPS, SSL/TLS, and security headers for {sessionStorage.getItem('domain')}</p>
                        </div>
                    </div>
                </div>

                {/* Info Section */}
                <div className="web-security-info-box">
                    <div className="web-security-info-header">
                        <div className="web-security-info-icon-wrapper">
                            <Info className="web-security-info-icon" />
                        </div>
                        <h3>About This Assessment</h3>
                    </div>
                    <div className="web-security-info-content">
                        <p>
                            This comprehensive security assessment evaluates your website's security posture across multiple dimensions
                            including Content Security Policy (CSP), HTTPS implementation, and SSL/TLS configuration.
                        </p>
                        <p>
                            The assessment uses industry-standard tools (pshtt, sslyze) to identify security vulnerabilities
                            and configuration issues that could impact your site's security.
                        </p>
                    </div>
                </div>

                {/* CSP Status Card */}
                <div className="csp-status-card">
                    <div className="csp-status-header">
                        <ShieldCheck className="csp-status-header-icon" />
                        <h3>Content Security Policy (CSP) Status</h3>
                    </div>
                    <div className="csp-status-content">
                        <div className={`csp-status-icon-wrapper ${isCspEnabled ? 'enabled' : 'disabled'}`}>
                            {isCspEnabled ? (
                                <CheckCircle className="csp-status-icon enabled" />
                            ) : (
                                <XCircle className="csp-status-icon disabled" />
                            )}
                        </div>
                        <div className="csp-status-text">
                            <div className="csp-status-label">CSP Configuration</div>
                            <div className={`csp-status-value ${isCspEnabled ? 'enabled' : 'disabled'}`}>
                                {contentSecurityPolicy?.toString() === 'true' ? 'Enabled' : 'Not Enabled'}
                            </div>
                            <div className={`csp-status-badge ${isCspEnabled ? 'enabled' : 'disabled'}`}>
                                {isCspEnabled ? (
                                    <>
                                        <CheckCircle className="csp-status-badge-icon" />
                                        <span>CSP Active</span>
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="csp-status-badge-icon" />
                                        <span>CSP Not Active</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="web-security-summary">
                    <div className="web-security-summary-card">
                        <div className="web-security-summary-icon-wrapper green">
                            <ShieldCheck className="web-security-summary-icon green" />
                        </div>
                        <div className="web-security-summary-content">
                            <h3>{isCspEnabled ? 'Active' : 'Inactive'}</h3>
                            <p>CSP Status</p>
                        </div>
                    </div>

                    <div className="web-security-summary-card">
                        <div className="web-security-summary-icon-wrapper blue">
                            <Network className="web-security-summary-icon blue" />
                        </div>
                        <div className="web-security-summary-content">
                            <h3>{pshttData.columns ? pshttData.columns.length : '0'}</h3>
                            <p>HTTPS Checks</p>
                        </div>
                    </div>

                    <div className="web-security-summary-card">
                        <div className="web-security-summary-icon-wrapper purple">
                            <Lock className="web-security-summary-icon purple" />
                        </div>
                        <div className="web-security-summary-content">
                            <h3>{sslyzeData.columns ? sslyzeData.columns.length : '0'}</h3>
                            <p>SSL/TLS Tests</p>
                        </div>
                    </div>
                </div>

                {/* Pshtt Scan Data */}
                <div className="web-security-data-section">
                    <div className="web-security-section-header">
                        <Network className="section-header-icon" />
                        <h2>PSHTT (HTTPS & HSTS) Analysis</h2>
                        <span className="scan-id">Scan ID: {scanId}</span>
                    </div>
                    {pshttData && pshttData.rows ? (
                        <DataTableWithPopup noBottomColumns thirdPartyData={pshttData} />
                    ) : (
                        <div className="web-security-empty-state">
                            <Network className="web-security-empty-icon" />
                            <p>No PSHTT scan data available</p>
                        </div>
                    )}
                </div>

                {/* Sslyze Scan Data */}
                <div className="web-security-data-section">
                    <div className="web-security-section-header">
                        <Lock className="section-header-icon" />
                        <h2>SSLyze (SSL/TLS) Configuration</h2>
                        <span className="scan-id">Scan ID: {scanId}</span>
                    </div>
                    {sslyzeData && sslyzeData.rows ? (
                        <DataTableWithPopup noBottomColumns thirdPartyData={sslyzeData} />
                    ) : (
                        <div className="web-security-empty-state">
                            <Lock className="web-security-empty-icon" />
                            <p>No SSLyze scan data available</p>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment >
    )
}
export default connect(null, { setBreadcrumbItems })(WebSecurity)